import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Divider, Typography } from '@mui/material';
import { SBSection } from '@dndModels';
import { DataType } from '@dndModels/Types';
import API from '@utilities/smart_api';
import DisplayStringWithReplacementsRecursive from './display/DisplayStringWithReplacementsRecursive';

const api = API.getInstance();

interface SBSectionContentViewerProps {
  section: SBSection;
  inSinglePage?: boolean;
  onAllLoaded?: (sectionId: string) => void;
}

export default function SBSectionContentViewer({
  section,
  inSinglePage = false,
  onAllLoaded,
}: SBSectionContentViewerProps) {
  const [loadedSection, setLoadedSection] = useState<SBSection | null>(
    section.loaded ? section : null
  );
  const [loading, setLoading] = useState(!section.loaded);

  const childCount = inSinglePage ? section.subsections.length : 0;

  // Everything needed for the "all done" check lives in refs so nothing
  // goes stale across renders.
  const loadedChildIds = useRef<Set<string>>(new Set());
  const selfDoneRef = useRef(section.loaded);
  const onAllLoadedRef = useRef(onAllLoaded);
  onAllLoadedRef.current = onAllLoaded; // always current, no dep issues

  // ---------------------------------------------------------------------------
  // Load own content
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (section.loaded) {
      setLoadedSection(section);
      setLoading(false);
      selfDoneRef.current = true;
      tryFireAllLoaded();
      return;
    }
    if (section._id === '') {
      setLoadedSection(section);
      setLoading(false);
      selfDoneRef.current = true;
      tryFireAllLoaded();
      return;
    }
    setLoading(true);
    // if (section.name.includes("Using")) {
    //   debugger;
    // }
    api.getFullObject2<SBSection>({ data_type: 'sb_section', id: section._id }).then(from_db => {
      if (from_db) {
        section.copy(from_db);
        setLoadedSection(section.clone());
      }
      selfDoneRef.current = true;
      setLoading(false);
      tryFireAllLoaded();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // ---------------------------------------------------------------------------
  // "All loaded" check — only uses refs, never goes stale
  // ---------------------------------------------------------------------------

  function tryFireAllLoaded() {
    if (!selfDoneRef.current) return;
    if (loadedChildIds.current.size < childCount) return;
    onAllLoadedRef.current?.(section.true_id);
  }

  function handleChildLoaded(childId: string) {
    loadedChildIds.current.add(childId);
    tryFireAllLoaded();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const renderContent = () => {
    if (loading || !loadedSection) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', marginBottom: 16 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading…</Typography>
        </div>
      );
    }
    return loadedSection.content ? (
      <DisplayStringWithReplacementsRecursive display_string={loadedSection.content} />
    ) : loadedSection.single_page ? ( // Don't show "No content." because it's going to show the content from the subsections.
      <></>
    ) : (
      <Typography variant="body2" style={{ color: '#888' }}>No content.</Typography>
    );
  };

  // console.log(section.true_id);
  return (
    <div id={`sb-section-${section.true_id}`}>
      <Typography variant="h5" gutterBottom>
        {section.name || <em style={{ color: '#888' }}>Untitled Section</em>}
      </Typography>
      <Divider style={{ marginBottom: 16 }} />
      {renderContent()}
      <Divider style={{ marginTop: 8, marginBottom: 16 }} />
      {inSinglePage && section.subsections.map(sub => {
        return (
          <SBSectionContentViewer
            key={sub.true_id}
            section={sub}
            inSinglePage
            onAllLoaded={handleChildLoaded}
          />
        );
      })}
    </div>
  );
}